import { useState } from "react";
import { Bookmark, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { referralApi } from "@/lib/api";

export default function SavePreferredButton({ userId, provider, category, variant = "chip", className = "" }) {
  const [saved, setSaved] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const doSave = async () => {
    if (!userId) { setConfirming(false); return; }
    setSaving(true);
    try {
      await referralApi.savePreferred(userId, {
        providerId: provider.id, source: "self_selected", category,
        name: provider.name, city: provider.city, state: provider.state, address: provider.address, logoUrl: provider.logoUrl,
      });
      setSaved(true);
      setConfirming(false);
      toast.success(`${provider.name} added to your preferred providers`);
    } catch {
      setConfirming(false);
      toast.error("Could not add provider");
    } finally {
      setSaving(false);
    }
  };

  const onClick = (e) => {
    e.stopPropagation();
    if (!saved) setConfirming(true);
  };

  return (
    <>
      {variant === "icon" ? (
        <button onClick={onClick} className={`inline-flex items-center justify-center rounded-full ${className}`} data-testid={`save-preferred-${provider.id}`} title={saved ? "Saved" : "Save to preferred"}>
          <Bookmark className={`h-5 w-5 ${saved ? "fill-emerald-500 text-emerald-500" : "text-slate-400"}`} />
        </button>
      ) : (
        <Button variant="outline" size="sm" onClick={onClick} className={`rounded-full ${saved ? "border-emerald-500 text-emerald-600" : "border-primary text-primary"} ${className}`} data-testid={`save-preferred-${provider.id}`}>
          <Bookmark className={`h-4 w-4 ${saved ? "fill-emerald-500" : ""}`} /> {saved ? "Saved" : "Save"}
        </Button>
      )}

      <Dialog open={confirming} onOpenChange={(o) => !o && setConfirming(false)}>
        <DialogContent className="max-w-sm rounded-3xl text-center" data-testid="confirm-save-preferred-dialog">
          <div className="flex flex-col items-center">
            <span className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center"><Bookmark className="h-6 w-6 text-primary" /></span>
            <DialogHeader className="items-center mt-3">
              <DialogTitle className="text-center">Add to preferred providers?</DialogTitle>
              <DialogDescription className="text-center">
                <span className="font-bold text-primary block mt-1">{provider.name}</span>
                Confirm to add this under your preferred healthcare providers.
              </DialogDescription>
            </DialogHeader>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button variant="outline" className="rounded-2xl" onClick={() => setConfirming(false)} disabled={saving}>Cancel</Button>
            <Button className="rounded-2xl" onClick={doSave} disabled={saving} data-testid="confirm-save-preferred">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
